import React from 'react';

import { formatDuration, formatPrice, formatPricePerHour } from '@front/common/src/utils/formatting';
import { HourlyBasketPrices, Promotion } from '@front/common/src/types/commons';
import { BasketState } from '@front/common/src/ducks/common/basket';

import { shouldFormatPromotionPerHour as shouldFormatPromotionPerHourUtils } from '@front/customer/src/utils/basket';

import { translate } from '@front/i18n';

import Title from '@front/web-ui/src/components/base-text/variants/title';
import Text from '@front/web-ui/src/components/base-text/variants/text';

import ReductionLine from '../reduction-line';
import { CalculationWrapper, ChildcareCalculationLine, TotalLine } from '../common';

const t = translate('basket');

type HourlyCalculationPricesProps = {
  prices: HourlyBasketPrices;
  duration?: BasketState['duration'];
  promotion?: Promotion;
  showTaxCredit?: boolean;
};

const HourlyCalculationPrices = ({
  prices,
  duration,
  showTaxCredit = true,
  promotion,
}: HourlyCalculationPricesProps) => {
  const {
    hourlyPrice = 0,
    totalHourlyPrice = 0,
    promoValue,
    totalHourlyPriceAfterTax,
    totalPrice,
    giftValue,
  } = prices;
  const durationInHours = duration ? formatDuration(duration) : undefined;
  const shouldFormatPromotionPerHour = shouldFormatPromotionPerHourUtils({ promotion, duration });

  return (
    <>
      <CalculationWrapper>
        {totalHourlyPrice > hourlyPrice && (
          <ChildcareCalculationLine>
            <Text.MDBold>{t('.total')}</Text.MDBold>
            <Text.MDBold>{formatPricePerHour(totalHourlyPrice)}</Text.MDBold>
          </ChildcareCalculationLine>
        )}
        {!!promoValue && (
          <ReductionLine
            title={t('.promo_code')}
            value={shouldFormatPromotionPerHour ? promotion!.amount : promoValue}
            shouldFormatPricePerHour={shouldFormatPromotionPerHour}
          />
        )}
        {!!giftValue && <ReductionLine title={t('.gift_card')} value={giftValue} />}
        {showTaxCredit && (
          <ChildcareCalculationLine>
            <Text>{t('.after_tax_credit')}</Text>
            <Text>{formatPricePerHour(totalHourlyPriceAfterTax)}</Text>
          </ChildcareCalculationLine>
        )}
      </CalculationWrapper>
      {durationInHours && (
        <TotalLine>
          <Title.MD>{formatPrice(totalPrice)}</Title.MD>
          <Text.MDBold>{t('.total_duration', { duration: durationInHours })}</Text.MDBold>
        </TotalLine>
      )}
    </>
  );
};

export default HourlyCalculationPrices;
